(() => {
  'use strict';
  const key = Symbol.for('seldravinpack.collectionTabs.initialize');
  if (window[key]) { window[key](document); return; }
  const instances = new Map();
  function create(root) {
    const config = JSON.parse(root.querySelector('[data-products-config]').textContent);
    const tabs = [...root.querySelectorAll('[data-tab]')];
    const panels = [...root.querySelectorAll('[data-tab-panel]')];
    const mobile = matchMedia('(max-width:767px)');
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    const controller = new AbortController();
    const listen = (node, type, fn) => node.addEventListener(type, fn, { signal: controller.signal });
    let active = 0, slider = null, animation = null, opener = null;
    function reset() {
      slider?.destroy(true, true); slider = null;
      panels.forEach(panel => {
        panel.querySelector('[data-products-carousel]').classList.remove('swiper');
        const grid = panel.querySelector('.product-grid');
        grid.classList.remove('swiper-wrapper');
        [...grid.children].forEach(card => card.classList.remove('swiper-slide'));
      });
    }
    function sync() {
      reset();
      const panel = panels[active];
      if (!panel || !mobile.matches || typeof window.Swiper !== 'function') return;
      const host = panel.querySelector('[data-products-carousel]');
      const grid = host.querySelector('.product-grid');
      const cards = [...grid.querySelectorAll('.product-card')];
      if (!cards.length) return;
      const view = Math.max(1, Number(config.slidesPerView) || 1.2);
      host.classList.add('swiper'); grid.classList.add('swiper-wrapper');
      cards.forEach(card => card.classList.add('swiper-slide'));
      slider = new window.Swiper(host, {
        slidesPerView: view, spaceBetween: Math.max(0, Number(config.spaceBetween) || 0),
        loop: config.loop === true && cards.length >= Math.ceil(view) + 1,
        speed: reduced.matches ? 0 : 320, watchOverflow: true, a11y: { enabled: true }
      });
    }
    function select(index, focus = false) {
      if (!tabs[index]) return;
      animation?.cancel(); active = index;
      tabs.forEach((tab, i) => {
        tab.classList.toggle('active', i === index);
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        panels[i].hidden = i !== index;
      });
      sync();
      if (focus) tabs[index].focus();
      if (!reduced.matches) animation = panels[index].animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
    }
    tabs.forEach((tab, index) => {
      listen(tab, 'click', () => select(index));
      listen(tab, 'keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== undefined) { event.preventDefault(); select(next, true); }
      });
    });
    listen(mobile, 'change', sync);
    listen(reduced, 'change', () => { animation?.cancel(); sync(); });
    listen(root, 'shopify:block:select', event => select(tabs.findIndex(tab => tab.dataset.blockId === String(event.detail?.blockId))));
    listen(root, 'click', event => {
      const choose = event.target.closest('[data-choose-options]');
      if (choose && typeof HTMLDialogElement !== 'undefined') {
        const dialog = root.querySelector(`[id="${CSS.escape(choose.dataset.chooseOptions)}"]`);
        if (dialog?.showModal) { event.preventDefault(); opener = choose; dialog.showModal(); }
      }
      if (event.target.closest('[data-close-dialog]')) event.target.closest('dialog').close();
      const step = event.target.closest('[data-quantity-step]');
      if (step) {
        const input = step.parentElement.querySelector('input');
        input.value = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.quantityStep));
      }
    });
    root.querySelectorAll('dialog.product-dialog').forEach(dialog => {
      listen(dialog, 'close', () => { if (opener?.isConnected && !document.querySelector('[data-cart-drawer][open]')) opener.focus(); });
      listen(dialog, 'click', event => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
      });
    });
    listen(root, 'change', event => {
      if (!event.target.matches('[data-variant-select]')) return;
      const form = event.target.form, option = event.target.selectedOptions[0];
      form.querySelector('[data-variant-price]').textContent = option.dataset.price;
      form.querySelector('[type="submit"]').disabled = option.dataset.available !== 'true';
      form.querySelector('[data-add-label]').textContent = option.dataset.available === 'true' ? config.add : config.soldOut;
      form.querySelector('[data-form-status]').hidden = true;
    });
    listen(root, 'submit', async event => {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      event.preventDefault();
      if (form.dataset.busy || !form.reportValidity()) return;
      form.dataset.busy = 'true';
      const button = form.querySelector('[type="submit"]');
      const label = button.querySelector('[data-add-label]');
      const status = form.querySelector('[data-form-status]');
      button.disabled = true; label.textContent = config.adding;
      status.hidden = true; form.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(form.action, location.href);
        url.pathname = url.pathname.replace(/\/$/, '') + '.js';
        const response = await fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form), signal: controller.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : config.error);
        status.textContent = config.added; status.hidden = false;
        if (document.querySelector('[data-cart-drawer]')) {
          document.dispatchEvent(new CustomEvent('cart:added', { detail: { opener: form.closest('dialog') ? opener : button } }));
          return;
        }
        // The addition has succeeded even if the separate cart refresh fails.
        try {
          const cartResponse = await fetch(config.cartUrl + '.js', { headers: { Accept: 'application/json' }, signal: controller.signal });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
            document.querySelectorAll('.cart-trigger').forEach(el => { el.setAttribute('aria-label', config.cartLabel.replace('{count}', cart.item_count)); });
            document.querySelectorAll('[data-cart-total]').forEach(el => { el.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cart.currency }).format(cart.total_price / 100); });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          }
        } catch { /* Keep the successful add result; never repeat the mutation. */ }
      } catch (error) {
        if (error.name !== 'AbortError') { status.textContent = error.message || config.error; status.hidden = false; }
      } finally {
        delete form.dataset.busy; form.removeAttribute('aria-busy');
        button.disabled = false; label.textContent = config.add;
      }
    });
    listen(root, 'focusin', event => {
      const card = event.target.closest('.swiper-slide');
      if (!slider || !card) return;
      const index = [...card.parentElement.children].indexOf(card);
      if (!slider.params.loop) slider.slideTo(index);
    });
    sync();
    return { destroy() { controller.abort(); animation?.cancel(); reset(); root.querySelectorAll('dialog.product-dialog[open]').forEach(dialog => dialog.close()); } };
  }
  function initialize(scope) {
    const roots = [...scope.querySelectorAll('[data-collection-tabs], [data-product-catalog]')];
    if (scope.matches?.('[data-collection-tabs], [data-product-catalog]')) roots.unshift(scope);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, create(root)); });
  }
  function remove(root) { instances.get(root)?.destroy(); instances.delete(root); }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => { [...instances.keys()].forEach(root => { if (event.target === root || event.target.contains(root)) remove(root); }); });
  new MutationObserver(() => { [...instances.keys()].forEach(root => { if (!root.isConnected) remove(root); }); }).observe(document.documentElement, { childList: true, subtree: true });
  window[key] = initialize;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
})();
